import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
  ForwardedRef,
} from "react";
import { v4 as uuidv4 } from "uuid";
import DatePickerField from "controls/global/datepicker";
import {
  ActionResult,
  FileUploadAdditionalInfo,
  FileUploadStatus,
  TitleSearchProduct,
} from "entities/UIModel";
import {
  ActionResultStatus,
  DocumentType,
  ProgressImageType,
  UIConstants,
} from "utils/data/enum";
import {
  Box,
  Grid,
  styled,
} from "@mui/material";
import TextInputField from "controls/global/text-input-field";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import {
  gaps,
  padding,
  margin,
} from "theme/defaultStyle";
import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import {
  FileRejection,
  FileWithPath
} from "react-dropzone";
import ActionButton from "controls/global/action-button";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import {
  getFormattedErrorMessage,
  mapFileUploaResponse
} from "pages/file/utils/fileUploadHelper";
import { useDocument } from "utils/context/DocumentContext";
import { Document } from "entities/UIModel/Document";
import UploadStatusGrid from "./upload-files/UploadStatusGrid";
import {
  StewartDialog,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";
import {
  IconButton,
  Typography,
} from "@mui/material";
import OrderUpdateIcon from "theme/icons/OrderUpdateIcon";
import CloseIcon from "@mui/icons-material/Close";
import { useMessage } from "utils/context/TitleMessageContext";
import {
  colors,
  gradients,
  fontSize,
  fontWeight,
  borderRadius,
} from "theme/defaultStyle";
import { useWatch } from "react-hook-form";
import ConfirmationDialogV3 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV3";
import { useTitleSearchMessageIntegrationKey } from "utils/context/TitleSearchContext";
import { showToast } from "controls/global/stewart-toast/showToast";


const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: gaps.large1,
  paddingBottom: padding.xlarge1,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const GridContainer = styled(Box)({
  "& .MuiGrid-root.MuiGrid-item": {
    paddingBottom: `${padding.medium1} !important`,
  },
  "& .drop-zone": {
    marginTop: `${margin.zero} !important`,
  },
  "& .button": {
    textAlign: "right",
  }
});

const ButtonsContainer = styled(Box)({
  alignItems: "center",
  alignSelf: "center",
  display: "flex",
  flexDirection: "row",
  gap: gaps.small1,
  height: "100%",
  justifyContent: "flex-end",
  justifySelf: "flex-end",
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "calc(50vw)",
    minWidth: "calc(45vw)",
    display: "flex",
    justifyContent: "flex-start",
    padding: `${padding.xlarge21_5} ${padding.xlarge22} ${padding.xlarge22}`,
    overflowX: "auto",
  },
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
});

const StyledDocumentWrapper = styled("div")({
  padding: `${padding.xlarge1} ${padding.zero} ${padding.zero}`,
});

const UploadHeading = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold1,
  textTransform: "capitalize",
});

export type OrderUpdateDialogHandle = {
  closeDialog: () => void;
}

interface Props {
  productIndex: number;
  titleSearchProduct: TitleSearchProduct;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const OrderUpdateDialog =  ({ isOpen, productIndex, titleSearchProduct, onClose, onSubmit }: Props, ref: ForwardedRef<OrderUpdateDialogHandle>) => {
  const [open, setOpen] = useState<boolean>(false);
  const { setValue, getValues, trigger, clearErrors } = useFormWrapper();
  const [, { uploadFiles, deleteDocument }] = useDocument();
  const [hasFailedUpload, setHasFailedUpload] = useState<boolean>(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  // upload states
  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);
  const [isConfirmatinDialogOpen, setConfirmationDialogOpen] = useState(false);
  const isProgressCompletedRef = useRef<boolean>(false);
  const successUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const failedUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const uploadSessionRef = useRef<string>("");

  // control show progress 
  const [, { setBypassDefaultLoader, setPercentageComplete }] = useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [runProgress, setRunProgress] = useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = useState<string>("");

  const tobeDeletedDocumentId = useRef<number | undefined>(undefined);
  const currentProduct: TitleSearchProduct = getValues(`titleSearchProducts.${productIndex}`);
  const DEFAULT_MESSAGE = "Delete";
  const [isDateFieldValid, setIsDateFieldValid] = useState<boolean>(false);
  const [, { deleteDocuments }] = useMessage();
  const [fileUploadStatuses, setFileUploadStatuses] = useState<FileUploadStatus[]>([]);
  const [{ messageIntegrationKey }, { setMessageIntegrationKey }] = useTitleSearchMessageIntegrationKey();

  const fileId: number = getValues("id"); // || 0;
  const orderId: number = getValues(`titleSearchProducts.${productIndex}.orderID`); // || 0;

  useImperativeHandle(ref, () => ({
    closeDialog: () => {
      setOpen(false);
    }
  }));

  useEffect(() => {
    uploadSessionRef.current = uuidv4();
  }, []);

  const messageNeedByDateField = useWatch({
    name: `titleSearchProducts.${productIndex}.messageNeedByDate`
  });

  const messageTextField = useWatch({
    name: `titleSearchProducts.${productIndex}.productActionMessageText`
  });

  const isSendDisabled = useMemo(() => {
    return (
      titleSearchProduct.integrationKey === getValues(`titleSearchProducts.${productIndex}.integrationKey`)
      &&
      (!messageNeedByDateField || messageTextField?.trim().length === 0 || messageTextField.length > 2500 || !isDateFieldValid)
    );
  }, [messageNeedByDateField, messageTextField, isDateFieldValid]);


  async function handleValidation(validDate?: Date | null) {
    const isValid = await validateNeedByDateField();
    if (isValid)
      setIsDateFieldValid(true);
    else
      setIsDateFieldValid(false);
  }

  const validateNeedByDateField = async () => {
    clearErrors(`titleSearchProducts.${productIndex}.messageNeedByDate`);
    setValue(`titleSearchProducts.${productIndex}.isUpdateOrderValidationRequired`, true);
    return await trigger(`titleSearchProducts.${productIndex}.messageNeedByDate`);
  }

  const handleDateFromChange = (newDate: Date | null) => {
    setValue(`titleSearchProducts.${productIndex}.isUpdateOrderValidationRequired`, false);
    if (newDate) {
      if (new Date(newDate).toString() === 'Invalid Date') {
        setValue(`titleSearchProducts.${productIndex}.messageNeedByDate`, null);
      }
    }
    handleValidation(newDate);
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    tobeDeletedDocumentId.current = undefined;
  }

  const handleDialogCancel = () => {
    setConfirmationDialogOpen(false);
    tobeDeletedDocumentId.current = undefined;
  };

  const setNotDefault = () => {
    setValue(`titleSearchProducts.${productIndex}.isDefault`, false);
  };

  useEffect(() => {
    setOpen(isOpen);
    if (isOpen && productIndex !== undefined) {
      clearErrors();
      successUploadFilesRef.current = [];
      failedUploadFilesRef.current = [];
      isProgressCompletedRef.current = false;
      setValue(`titleSearchProducts.${productIndex}.productActionMessageText`, "");
      setValue(`titleSearchProducts.${productIndex}.messageNeedByDate`, "");
      setValue(`titleSearchProducts.${productIndex}.messageIntegrationKey`, "");
      setValue(`titleSearchProducts.${productIndex}.messageIntegrationKey`, uuidv4());
      setMessageIntegrationKey(currentProduct.messageIntegrationKey);
    }
  }, [isOpen]);

  const handleOnUpload = async (acceptedFiles: FileWithPath[], rejectedFiles: FileRejection[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const acceptedStatuses: FileUploadStatus[] = acceptedFiles.map((file, index) => ({
        documentId: Date.now() + index,
        documentOriginalFileName: file.name,
        errorMessage: "",
        isUploadSuccess: true,
        documentIndex: index,
      }));
      setFileUploadStatuses((prev) => [...prev, ...acceptedStatuses]);
    }
    if (rejectedFiles && rejectedFiles.length > 0) {
      const rejectedStatuses: FileUploadStatus[] = rejectedFiles.map((file, index) => ({
        documentId: Date.now() + index,
        documentOriginalFileName: "",
        errorMessage: getFormattedErrorMessage(file.errors),
        isUploadSuccess: false,
        documentIndex: index,
      }));
      setFileUploadStatuses((prev) => [...prev, ...rejectedStatuses]);
    }

    // -- Show upload in progress
    setUploadCompleted(false);
    const progressRequestId = uuidv4();
    setProgressRequestId(progressRequestId);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);

    isProgressCompletedRef.current = false;

    // const fileId: number = getValues("id") || 0;
    // const orderId: number = getValues(`titleSearchProducts.${productIndex}.orderID`) || 0;
    const processedStatusTypeCode: string = getValues(`titleSearchProducts.${productIndex}.processedStatusTypeCode`);

    // -- Prepare for upload
    let fileAdditionalInfos: FileUploadAdditionalInfo[] = [];
    const formData = new FormData();

    const agency = getValues("agency");
    const companyID = agency?.id || "";
    const clientFileID = getValues("fileNameNumber") || "";
    const companyLegacyID = agency?.legacyID || "";

    formData.set("companyID", companyID);
    formData.set("fileID", fileId.toString());
    formData.set("orderID", orderId.toString());
    formData.set("processedStatusTypeCode", processedStatusTypeCode || "");
    formData.set("uploadSessionKey", uploadSessionRef.current);
    formData.set("clientFileID", clientFileID);
    formData.set("companyLegacyID", companyLegacyID);

    let index = 0;
    acceptedFiles?.forEach((file) => {
      formData.append("files", file, file.name);
      fileAdditionalInfos.push({
        index: index,
        filePath: file.path || "",
        fileName: file.name,
        fileSize: file.size,
        errorMessage: "",
        isValidFile: true,
      });
      index++;
    });

    rejectedFiles?.forEach((rejectedFile, i) => {
      fileAdditionalInfos.push({
        index: index,
        filePath: "", // not available
        fileName: rejectedFile.file.name,
        fileSize: rejectedFile.file.size,
        errorMessage: getFormattedErrorMessage(rejectedFile.errors),
        isValidFile: false,
      });
      index++;
    });

    formData.append("fileUploadAdditionalInfo", JSON.stringify(fileAdditionalInfos));

    // -- Start upload files
    // const uploadedResult = await uploadFiles(progressRequestId, formData, DocumentType.TITLEDOCS); // DocumentType.TITLESEARCHDOC);
    const actionResult: ActionResult = await uploadFiles(progressRequestId, formData, DocumentType.TITLEDOCS); // DocumentType.TITLESEARCHDOC);

    if (actionResult.status === ActionResultStatus.Success) {
      const uploadedResult = actionResult.apiResponse;
      const mappedFileUploadStatuses = mapFileUploaResponse(uploadedResult);

      successUploadFilesRef.current = [
        ...(successUploadFilesRef.current || []),
        ...mappedFileUploadStatuses.filter((f) => f.isUploadSuccess),
      ];
      const newFailures = mappedFileUploadStatuses && mappedFileUploadStatuses.filter((f) => !f.isUploadSuccess);
      if (newFailures.length > 0) {
        failedUploadFilesRef.current = [
          ...(failedUploadFilesRef.current || []),
          ...newFailures,
        ];
        setHasFailedUpload(true);
      }
    }
    else {
      showToast({
        type: 'error',
        title: UIConstants.UNEXPECTED_ERROR_MESSAGE.toString(),
      });  
    }

    setPercentageComplete(progressRequestId);
    setUploadCompleted(true);
  };

  const handleSubmitUpdateOrder = async () => {
    setNotDefault();
    const uploadedDocumentsArray: Document[] = successUploadFilesRef.current.map(document => ({
      fileID: fileId,
      orderID: orderId,
      documentID: document.documentId,
      documentOriginalFileName: document.documentOriginalFileName,
      // errorMessage: document.errorMessage,
      // isUploadSuccess: document.isUploadSuccess,
      uniqueIdentifier: "",
      integrationKey: uuidv4(),
    }));
    if (messageIntegrationKey)
      setValue(`titleSearchProducts.${productIndex}.messageIntegrationKey`, messageIntegrationKey);

    if (uploadedDocumentsArray && uploadedDocumentsArray.length > 0)
      setValue(`titleSearchProducts.${productIndex}.messageDocuments`, uploadedDocumentsArray);
    setValue(`titleSearchProducts.${productIndex}.isUpdateOrderValidationRequired`, false);
    onSubmit();
  }

  // Upload related function
  const handleOnCloseProgressDialog = async () => {
    isProgressCompletedRef.current = true;
    if (uploadCompleted) {
      setOpenProgressDialog(false);
      setRunProgress(false);
      setProgressRequestId("");
    }
  }

  useEffect(() => {
    if (uploadCompleted && openProgressDialog)
      handleOnCloseProgressDialog();
  }, [uploadCompleted])

  const handleOnDelete = async (documentId: number) => {
    tobeDeletedDocumentId.current = documentId;
    const tobeDeletedFile = successUploadFilesRef.current.find((f) => f.documentId === documentId) ??
      fileUploadStatuses.find((f) => f.documentId === documentId); // fallback to state
    if (!tobeDeletedFile) {
      setConfirmationMessage(`Delete this document?`);
    } else {
      setConfirmationMessage(
        `${DEFAULT_MESSAGE} '${tobeDeletedFile.documentOriginalFileName}'?`
      );
    }
    setConfirmationDialogOpen(true);
  };

  const deleteUploadedImage = async () => {
    if (tobeDeletedDocumentId.current) {
      await deleteDocument(tobeDeletedDocumentId.current);

      // Update both ref and state
      successUploadFilesRef.current = successUploadFilesRef.current.filter(
        (f) => f.documentId !== tobeDeletedDocumentId.current
      );
      setFileUploadStatuses((prev) =>
        prev.filter((f) => f.documentId !== tobeDeletedDocumentId.current));
    }
    setConfirmationDialogOpen(false);
  };

  const handleClickOnDone = async () => {
    onClose && onClose();
    clearErrors();
    setValue(`titleSearchProducts.${productIndex}.isUpdateOrderValidationRequired`, false);
    setValue(`titleSearchProducts.${productIndex}.messageIntegrationKey`, "");
    const uploadedDocs = successUploadFilesRef.current;
    const documentIdsToDelete = uploadedDocs
      .filter((doc) => doc.isUploadSuccess && doc.documentId)
      .map((doc) => doc.documentId);
    if (documentIdsToDelete.length > 0) {
      try {
        await deleteDocuments(documentIdsToDelete);
      } catch (err) {
        console.error("Failed to delete uploaded documents:", err);
      }
    }
    successUploadFilesRef.current = [];
    failedUploadFilesRef.current = [];
    setFileUploadStatuses([]);
    setOpenProgressDialog(false);
    setMessageIntegrationKey("");
  };

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle
          id="styledDialogTitleDebug"
        >
          <IconContainer>
            <OrderUpdateIcon />
          </IconContainer>
          <Typography id="uploadPolicyImageTitleDebug">Order Update</Typography>
          <CloseIconContainer>
            <IconButton
              className="iconButton"
              onClick={handleClickOnDone}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>
        <GridContainer>
          <Grid container={true} spacing={1.5}>
            <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
              <DatePickerField
                validateOnBlur={false}
                defaultValue={undefined}
                minDate={new Date()}
                name={`titleSearchProducts.${productIndex}.messageNeedByDate`}
                onBlurCallback={handleDateFromChange}
                onChangeCallback={handleDateFromChange}
                showError={true}
                label="Need By Date"
              />
            </Grid>
            <Grid item={true} xs={12} md={8} lg={8} xl={8}>
              <TextInputField
                errorMaxLength={2500}
                errorMessageExceedLimit={"Cannot exceed 2,500 characters"}
                multiline
                name={`titleSearchProducts.${productIndex}.productActionMessageText`}
                placeholder="Message"
                label="Message"
                isRequired={true}
                requiredMessage="Required"
              />
            </Grid>
            <Grid item={true} xs={12} md={12} lg={12} xl={12}>
              <UploadDropZone
                documentType={DocumentType.TITLEDOCS}
                handleOnUpload={handleOnUpload}
              />
              {fileUploadStatuses.length > 0 && (
                <Box mt={1}>
                  {successUploadFilesRef.current.length > 0 && (
                    <StyledDocumentWrapper>
                      <UploadHeading
                        gutterBottom>
                        Uploaded Documents ({successUploadFilesRef.current.length})
                      </UploadHeading >
                      <UploadStatusGrid
                        fileUploadStatuses={successUploadFilesRef.current}
                        handleOnDelete={handleOnDelete}
                      />
                    </StyledDocumentWrapper>
                  )}
                  {hasFailedUpload && failedUploadFilesRef.current?.length > 0 && (
                    <StyledDocumentWrapper>
                      <UploadHeading className="failed-upload-status" gutterBottom>
                        Failed to Upload ({failedUploadFilesRef.current?.length})
                      </UploadHeading>
                      <UploadStatusGrid fileUploadStatuses={failedUploadFilesRef.current}
                      />
                    </StyledDocumentWrapper>
                  )}
                </Box>
              )}
            </Grid>
            <Grid item={true} xs={12} md={12} lg={12} xl={12}>
              <ButtonsContainer>
                <ActionButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    handleClickOnDone();
                  }}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  color="primary"
                  onClick={handleSubmitUpdateOrder}
                  disabled={isSendDisabled}
                >
                  Order Update
                </ActionButton>
              </ButtonsContainer>
            </Grid>
          </Grid>
        </GridContainer>
        <ConfirmationDialogWithProgressbar
          autoClose={true}
          title="Please wait while uploading your documents ..."
          closeButtonText="Done! Click to Continue"
          isOpen={openProgressDialog}
          onClose={handleOnCloseProgressDialog}
          requestId={progressRequestId}
          runProgress={runProgress}
          progressImageType={ProgressImageType.circular}
          delay={1000} // 1000 miliseconds - call processStatus every 1 second 
          maxDelayCounter={90} // max delay: 90 x 1000 miliseconds = 90 seconds - close progress maximum in 90 seconds + response time
          maxZeroPctCompleteCount={30} //  close 0 percentage dialog after about 30 seconds + response times for each call; where 30 count x delay (1000 miliseconds ) + response for each call
        />
        <ConfirmationDialogV3
          isOpen={isConfirmatinDialogOpen}
          confirmationMessage={confirmationMessage}
          cancelActionButton={true}
          onYes={() => deleteUploadedImage()}
          onNo={() => closeConfirmationDialog()}
          onCancel={handleDialogCancel}
          singleActionButtonText={"Delete"}
          buttonNoText={"Cancel"}
        />
      </StyledDialog>
    </>
  );
};

export default forwardRef<OrderUpdateDialogHandle, Props>(OrderUpdateDialog);
